import { QuestionTypes } from '@showbie-socrative/socrative-utils';
import nanoid from 'nanoid';

import { GenerateAIQuestionsEventAdapter } from '~analytics/adapters/GenerateAIQuestionsEventAdapter';

import localAxios from '../localAxios';
import { generateQuestionURL } from './urls';

interface GenerateQuestionParams {
  questionCount: number;
  topic: string;
  includeExplanation: boolean;
  questionTypes: Array<keyof typeof QuestionTypes>;
}

export interface AnswerResponse {
  order: number;
  text: string;
  isCorrect: boolean;
}

export interface QuestionResponse {
  questionType: QuestionTypes;
  questionText: string;
  explanation: string;
  answers: AnswerResponse[];
}

export interface GenerateQuestionResponse {
  questions: QuestionResponse[];
  elapsedTime: number;
  version: string;
  usage: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
}

export async function generateQuestions({
  questionCount,
  topic,
  questionTypes,
  includeExplanation,
}: GenerateQuestionParams) {
  const questionURL = generateQuestionURL();

  const {
    data: { questions, version, elapsedTime, usage },
  } = await localAxios.post<GenerateQuestionResponse>(questionURL.href, {
    questionCount,
    topic,
    questionTypes,
    includeExplanation,
  });

  // Analytics
  new GenerateAIQuestionsEventAdapter({
    numQuestions: questionCount,
    multipleChoice: questionTypes.includes('MultipleChoice'),
    trueFalse: questionTypes.includes('TrueFalse'),
    shortAnswer: questionTypes.includes('FreeResponse'),
    includeExplanation: includeExplanation,
    promptLength: topic.length,
    version: version,
    responseMilliseconds: elapsedTime,
    ...usage,
  }).sendAnalytics();

  return {
    questions: questions.map((question) => ({
      questionId: nanoid(),
      ...question,
    })),
    elapsedTime: elapsedTime,
    version: version,
  };
}
